import { CellContext } from '@tanstack/react-table';
import { CompanyCustomBenefitAssignmentEmployee } from '@pxtra-client-lib-legacy/gen';
import { useTranslation } from 'react-i18next';
import { ActionsMenuCell, ActionMenuItem } from '../../shared/actions-menu-cell';
import {
  BackwardIcon,
  ChatQuestionSupportIcon,
  CompaniesIcon,
  SingleUserIcon,
  UserDisabledIcon
} from '../../../../icons';
import { useCustomBenefitTableContext } from '../context';
import { supportEmail } from '../../../../../misc/globals';
import {
  getAssignmentType,
  getAssignmentId,
  getEditingStatus,
  getMaximumValidAmount
} from '../utils';

export const CustomBenefitActionsCell = ({
  row
}: CellContext<CompanyCustomBenefitAssignmentEmployee, unknown>) => {
  const { t } = useTranslation();
  const {
    assignmentsStart,
    companyAmount,
    createAssignments,
    deleteAssignments,
    setFocusedEmployeeId
  } = useCustomBenefitTableContext();
  const employee = row.original;
  const assignmentType = getAssignmentType(employee);
  const editingStatus = getEditingStatus(employee);
  const maximumValidAmount = getMaximumValidAmount(employee);

  const handleContactSupport = () => {
    const subject = encodeURIComponent('Custom Benefit Zuweisung');
    const body = encodeURIComponent(t('custom:contact_mail_intro'));
    window.open(`mailto:${supportEmail}?subject=${subject}&body=${body}`, '_blank');
  };

  const items: ActionMenuItem[] = [];

  if (assignmentType !== 'deactivated_by_CM' && assignmentType !== 'assignment_forbidden') {
    items.push({
      icon: <UserDisabledIcon color="black" />,
      label: t('common:DISABLE'),
      onClick: () => {
        if (employee.id) {
          createAssignments({
            startAccountingPeriod: assignmentsStart,
            amount: 0,
            employees: [employee.id]
          });
        }
      }
    });
  }

  if (assignmentType !== 'assignment_forbidden') {
    items.push({
      icon: <SingleUserIcon color="black" />,
      label: t('custom:assign_amount'),
      onClick: () => {
        setFocusedEmployeeId(employee.id);
      }
    });
  }

  if (
    ['invalid_global_assignment', 'invalid_individual_assignment', 'assignment_forbidden'].includes(
      assignmentType
    )
  ) {
    items.push({
      icon: <ChatQuestionSupportIcon color="black" />,
      label: t('custom:contact_support'),
      onClick: handleContactSupport
    });
  }

  if (['individual', 'deactivated_by_CM'].includes(assignmentType)) {
    const resetDisabled = !!maximumValidAmount && maximumValidAmount < (companyAmount ?? 0);

    items.push({
      icon: <CompaniesIcon />,
      label: t('custom:reset_assignment'),
      onClick: () => {
        if (employee.id) {
          createAssignments({
            startAccountingPeriod: assignmentsStart,
            amount: 0,
            employees: [employee.id],
            resetToCompanyAmount: true
          });
        }
      },
      disabled: resetDisabled,
      tooltipText: resetDisabled ? t('custom:reset_assignment_disabled_tooltip') : undefined
    });
  }

  if (
    (editingStatus === 'changed' ||
      (editingStatus === 'invalid' && assignmentType === 'invalid_individual_assignment')) &&
    assignmentType !== 'assignment_forbidden'
  ) {
    const assignmentId = getAssignmentId(employee);
    items.push({
      icon: <BackwardIcon color="error.main" />,
      label: t('custom:reset'),
      color: 'error.main',
      onClick: () => {
        if (assignmentId) {
          deleteAssignments({ assignmentIds: [assignmentId] });
        }
      }
    });
  }

  if (items.length === 0) return null;

  return <ActionsMenuCell items={items} />;
};
